import { AppBase } from '@jollywise/jollygoodgame';
import { KEYS, SCENE_MAP } from 'game/constants/SceneConstants';
import { ShortcutController } from './game/shortcuts/ShortcutController';
import PlayerModel from './game/model/PlayerModel';
import { CURSOR_UP, INTERACTIONS, SCENARIOS } from './game/constants/Constants';
import { VoModel } from 'game/model/VoModel.js';
import { CaptionsPlayer } from 'game/components/CaptionsPlayer.js';
import { HudConfig } from './game/constants/HudConfig';
import { Tracking } from 'game/tracking/Tracking.js';
import { TRACK_EVENTS, TRACK_SCREENS } from 'game/tracking/stats.js';

export default class App extends AppBase {
  constructor(opts) {
    super(opts);
    const params = new URLSearchParams(window.location.search);
    this._locale = params.get('locale') || opts.config.locale || 'en';
  }

  // bootGame is called when app is initialized and ready to start doing stuff
  bootGame() {
    console.log('IS DESKTOP : ' + this.device.os.desktop);
    // set base urls
    // grab the base assetpack
    const assetPack = require('../assets/assetpack.json');
    const bootkeys = ['boot'];
    // trigger game boot
    this.loader.locale = this._locale;
    this.loader.bootGame(
      {
        // boot should only load in assets required for the loadscreen to function
        boot: {
          assetpacks: [{ config: assetPack, keys: ['boot', 'boot_spritesheets', 'boot_locale'] }],
        },
        // remaining assets should be included in load so that progress is shown
        load: {
          assetpacks: [
            {
              config: assetPack,
              keys: Object.keys(assetPack).filter((key) => !bootkeys.includes(key)),
            },
          ],
        },
      },

      // trigger callback when load is complete
      this.assetsLoaded.bind(this)
    );
  }

  assetsLoaded() {
    this.input.setDefaultCursor(CURSOR_UP);
    this.hud.setHudConfig(HudConfig);
    this._setHudEvents();
    // plugin interaction scenes
    INTERACTIONS.forEach((interaction) =>
      this.scene.add(interaction.id, interaction.scene, false, { adasd: 'asd' })
    );
    // console.log('setVersionNumber', this.saves);
    this.saves.setVersionNumber('271023', true, true);
    // vo model, used to play vo
    const opts = {
      config: this.cache.json.get('vo_sprite_map'),
      soundController: this.soundController,
      enabled: this.settings.audio,
    };
    this._voModel = new VoModel(opts);
    this._captionsPlayer = new CaptionsPlayer(this, this.vo);
    this._stats = new Tracking(JW_DEBUG);
    this.player = new PlayerModel(this.saves);

    const hasShortcuts =
      JW_SHORTCUTS_ENABLED || /.*dev-.*fb-app-hosting.com/.test(window.location.href);
    if (!hasShortcuts) {
      this.shortcuts = null;
    }
    if (hasShortcuts) {
      this.shortcuts = new ShortcutController(this, this.shortcuts, true);
    }
    // register copy so that the copy parser can pick it up
    const copyxml = this.cache.xml.get('copy_xml');
    if (copyxml) this.copy.addCopyFromXML(copyxml);
    // add the scene map to scene controller so that it knows what you want
    this.sceneController.addSceneMap(SCENE_MAP);

    this.addBlurFocusListeners();
    // enter application

    // check query params for spine preview
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    if (urlParams.has('spinepreview')) {
      this.showSpinePreview();
    } else {
      this.showLanding();
    }

    // this.launchInteraction('star_breathing');
    // this.showWelcome();

    // this.launchScenario(1);
  }

  // =============================================================================================
  // SHORTCUTS
  // =============================================================================================
  addScenarioShortcuts() {
    this.shortcuts && this.shortcuts?.addScenarioShortcuts();
  }

  clearScenarioShortcuts() {
    this.shortcuts && this.shortcuts?.clearScenarioShortcuts();
  }

  // =============================================================================================
  // GETTERS
  // =============================================================================================
  get vo() {
    return this._voModel;
  }
  get captionsPlayer() {
    return this._captionsPlayer;
  }
  get stats() {
    return this._stats;
  }

  // =============================================================================================
  // TRACKING
  // =============================================================================================
  trackPromptStart(force = false) {
    force && this._stats.trackPromptStop();
    this._stats.trackPromptStart(force);
  }

  trackPromptStop() {
    this._stats.trackPromptStop();
  }

  // =============================================================================================
  // SCENES
  // =============================================================================================

  enterGame(viaButtonClick = true) {
    console.log('enterGame viaButtonClick', viaButtonClick);
    this.vo.stop();
    const buttonName = viaButtonClick ? 'PlayButton' : 'NotPlayButton';
    this.stats.track(TRACK_EVENTS.GAME_PLAY, { buttonName });
    this.showWelcome();
  }

  showSpinePreview() {
    this.sceneController.switchScene(KEYS.SpinePreview, { data: {} });
  }

  showLanding() {
    this.stats.setScreenName(TRACK_SCREENS.TITLE);
    this.sceneController.switchScene(KEYS.Landing, { data: {} });
    this.hud.setState('LANDING');
  }

  returnWelcome() {
    this.sceneController.switchScene(KEYS.Welcome, { data: {} });
    this.hud.setState('EMPTY');
    this.stats.track(TRACK_EVENTS.GAME_MENU_CLICK, { buttonName: 'Home' });
    this.stats.setScreenName(TRACK_SCREENS.MENU);
    this.stats.setRoundName(false);
  }
  showWelcome(opts) {
    this.stats.setScreenName(TRACK_SCREENS.MENU);
    this.sceneController.switchScene(KEYS.Welcome, { data: opts });
    this.hud.setState('EMPTY');
  }

  launchScenario(scenarioId) {
    const scenarioTrackingNames = {
      1: 'Blocks',
      2: 'Thunderstorm',
      3: 'Party',
      4: 'Drop-Off',
    };
    const buttonName = scenarioTrackingNames[scenarioId];
    this.stats.track(TRACK_EVENTS.GAME_MENU_CLICK, { buttonName });

    this.sceneController.switchScene(KEYS.Scenario, { data: { scenarioId } });
    this.hud.setState('DEFAULT');
  }

  completeScenario(scenarioId) {
    const roundNumber = this.player.getSessionScenarioPlayCount(scenarioId);
    this.player.addScenarioPlayed(scenarioId);
    this.stats.completeRound(roundNumber);
    const playthroughNumber = this.player.getScenarioPlayCount(scenarioId);
    this.stats.completeGamePlay(playthroughNumber);
    this.showWelcome({ completedScenario: true });
  }

  launchInteraction(interactionId) {
    this.sceneController.switchScene(KEYS.Interaction, { data: { interactionId } });
    this.hud.setState('DEFAULT');
  }

  // =============================================================================================
  // PAUSE / RESUME
  // =============================================================================================

  pauseGame() {
    super.pauseGame();
  }

  resumeGame() {
    super.resumeGame();
  }

  // =============================================================================================
  // BLUR / FOCUS
  // =============================================================================================
  addBlurFocusListeners() {
    // visibility change
    this.events.on('blur', () => {
      this.handleVisibilityChange({ type: 'blur' });
    });
    this.events.on('focus', () => {
      this.handleVisibilityChange({ type: 'focus' });
    });
  }

  handleVisibilityChange(e) {
    if (e.type == 'blur') {
      if (!this.paused) {
        this.paused = true;
        this.sceneController.pause();
        this.soundController.pause();
      }
    } else if (e.type == 'focus') {
      if (this.contextTimeout) {
        clearTimeout(this.contextTimeout);
        this.contextTimeout = null;
      }
      this.contextTimeout = setTimeout(() => {
        if (this.paused) {
          this.paused = false;
          this.sceneController.resume();
          this.soundController.resume();
        }
      }, 500);
    }
  }

  _setHudEvents() {
    this.hud.events.on('RETURN_HOME', this.returnWelcome, this);
    this.hud.events.on('ENTER_GAME', this.enterGame, this);
  }
}
